import { ParsedBrand, Brand } from '../types/brand'

export interface BrandCreateInput extends Omit<Brand, 'last_verified'> {
  last_verified?: string
}

export interface BrandUpdateInput extends Partial<BrandCreateInput> {
  brand: string // Brand name is required for updates (used as identifier)
}

export interface BrandQueryOptions {
  limit?: number
  offset?: number
  search?: string
  filters?: Record<string, any>
}

export interface BrandServiceResponse<T> {
  data: T
  success: boolean
  error?: string
  total?: number
}

export interface IBrandService {
  // Read operations
  getAllBrands(options?: BrandQueryOptions): Promise<BrandServiceResponse<ParsedBrand[]>>
  getBrandByName(brandName: string): Promise<BrandServiceResponse<ParsedBrand | null>>
  
  // Write operations
  createBrand(brand: BrandCreateInput): Promise<BrandServiceResponse<ParsedBrand>>
  updateBrand(brandName: string, updates: BrandUpdateInput): Promise<BrandServiceResponse<ParsedBrand>>
  deleteBrand(brandName: string): Promise<BrandServiceResponse<boolean>>
  
  // Bulk operations
  createBulk(brands: BrandCreateInput[]): Promise<BrandServiceResponse<ParsedBrand[]>>
  updateBulk(updates: { brandName: string; updates: BrandUpdateInput }[]): Promise<BrandServiceResponse<ParsedBrand[]>>
  deleteBulk(brandNames: string[]): Promise<BrandServiceResponse<boolean>>
  
  // Utility operations
  validateBrand(brand: BrandCreateInput): Promise<BrandServiceResponse<boolean>>
  searchBrands(query: string): Promise<BrandServiceResponse<ParsedBrand[]>>
  
  // Data management
  refreshData(): Promise<BrandServiceResponse<boolean>>
  exportData(format?: 'csv' | 'json'): Promise<BrandServiceResponse<string>>
}

export abstract class BaseBrandService implements IBrandService {
  abstract getAllBrands(options?: BrandQueryOptions): Promise<BrandServiceResponse<ParsedBrand[]>>
  abstract getBrandByName(brandName: string): Promise<BrandServiceResponse<ParsedBrand | null>>
  abstract createBrand(brand: BrandCreateInput): Promise<BrandServiceResponse<ParsedBrand>>
  abstract updateBrand(brandName: string, updates: BrandUpdateInput): Promise<BrandServiceResponse<ParsedBrand>>
  abstract deleteBrand(brandName: string): Promise<BrandServiceResponse<boolean>>
  abstract refreshData(): Promise<BrandServiceResponse<boolean>>
  
  // Default implementations for optional methods
  async createBulk(brands: BrandCreateInput[]): Promise<BrandServiceResponse<ParsedBrand[]>> {
    const results: ParsedBrand[] = []
    const errors: string[] = []
    
    for (const brand of brands) {
      try {
        const result = await this.createBrand(brand)
        if (result.success && result.data) {
          results.push(result.data)
        } else {
          errors.push(result.error || `Failed to create brand: ${brand.brand}`)
        }
      } catch (error) {
        errors.push(`Error creating brand ${brand.brand}: ${error}`)
      }
    }
    
    return {
      data: results,
      success: errors.length === 0,
      error: errors.length > 0 ? errors.join('; ') : undefined,
      total: results.length
    }
  }
  
  async updateBulk(updates: { brandName: string; updates: BrandUpdateInput }[]): Promise<BrandServiceResponse<ParsedBrand[]>> {
    const results: ParsedBrand[] = []
    const errors: string[] = []
    
    for (const { brandName, updates: brandUpdates } of updates) {
      try {
        const result = await this.updateBrand(brandName, brandUpdates)
        if (result.success && result.data) {
          results.push(result.data)
        } else {
          errors.push(result.error || `Failed to update brand: ${brandName}`)
        }
      } catch (error) {
        errors.push(`Error updating brand ${brandName}: ${error}`)
      }
    }
    
    return {
      data: results,
      success: errors.length === 0,
      error: errors.length > 0 ? errors.join('; ') : undefined,
      total: results.length
    }
  }
  
  async deleteBulk(brandNames: string[]): Promise<BrandServiceResponse<boolean>> {
    let successCount = 0
    const errors: string[] = []
    
    for (const brandName of brandNames) {
      try {
        const result = await this.deleteBrand(brandName)
        if (result.success) {
          successCount++
        } else {
          errors.push(result.error || `Failed to delete brand: ${brandName}`)
        }
      } catch (error) {
        errors.push(`Error deleting brand ${brandName}: ${error}`)
      }
    }
    
    return {
      data: successCount === brandNames.length,
      success: errors.length === 0,
      error: errors.length > 0 ? errors.join('; ') : undefined,
      total: successCount
    }
  }
  
  async validateBrand(brand: BrandCreateInput): Promise<BrandServiceResponse<boolean>> {
    const errors: string[] = []
    
    // Required field validation
    if (!brand.brand || brand.brand.trim() === '') {
      errors.push('Brand name is required')
    }
    
    // Enum validation
    const validAmazonPresence = ['none', 'available', 'brand_store', 'limited', 'unknown']
    if (!validAmazonPresence.includes(brand.amazon_presence)) {
      errors.push('Invalid amazon_presence value')
    }
    
    const validManufacturingVerification = ['confirmed_usa', 'likely_usa', 'mixed', 'imported', 'unknown']
    if (!validManufacturingVerification.includes(brand.manufacturing_verification)) {
      errors.push('Invalid manufacturing_verification value')
    }
    
    const validPriceTiers = ['budget', 'mid-range', 'premium', 'luxury', 'unknown']
    if (!validPriceTiers.includes(brand.price_tier)) {
      errors.push('Invalid price_tier value')
    }
    
    // Confidence score validation
    if (brand.confidence_score < 0 || brand.confidence_score > 10) {
      errors.push('Confidence score must be between 0 and 10')
    }
    
    // Founded year validation
    if (typeof brand.founded_year === 'number' && (brand.founded_year < 1000 || brand.founded_year > new Date().getFullYear())) {
      errors.push('Founded year must be a valid year')
    }
    
    return {
      data: errors.length === 0,
      success: true,
      error: errors.length > 0 ? errors.join('; ') : undefined
    }
  }
  
  async searchBrands(query: string): Promise<BrandServiceResponse<ParsedBrand[]>> {
    const allBrandsResult = await this.getAllBrands()
    if (!allBrandsResult.success) {
      return allBrandsResult
    }
    
    const searchLower = query.toLowerCase()
    const filtered = allBrandsResult.data.filter(brand => 
      brand.brand.toLowerCase().includes(searchLower) ||
      brand.consumer_summary.toLowerCase().includes(searchLower) ||
      brand.primary_products.toLowerCase().includes(searchLower) ||
      brand.clean_categories.some(cat => cat.toLowerCase().includes(searchLower))
    )
    
    return {
      data: filtered,
      success: true,
      total: filtered.length
    }
  }
  
  async exportData(format: 'csv' | 'json' = 'csv'): Promise<BrandServiceResponse<string>> {
    const allBrandsResult = await this.getAllBrands()
    if (!allBrandsResult.success) {
      return {
        data: '',
        success: false,
        error: allBrandsResult.error
      }
    }
    
    try {
      if (format === 'json') {
        return {
          data: JSON.stringify(allBrandsResult.data, null, 2),
          success: true
        }
      } else {
        // CSV format - convert back to original Brand interface format
        const csvData = allBrandsResult.data.map(brand => ({
          brand: brand.brand,
          website: brand.website,
          amazon_presence: brand.amazon_presence,
          manufacturing_verification: brand.manufacturing_verification,
          hq_location: brand.hq_location,
          mfg_locations: brand.mfg_locations.join(';'),
          primary_products: brand.primary_products,
          product_categories: brand.product_categories.join(';'),
          clean_categories: brand.clean_categories.join(','),
          purchase_methods: brand.purchase_methods,
          founded_year: brand.founded_year,
          business_type: brand.business_type,
          certifications: brand.certifications.join(';'),
          price_tier: brand.price_tier,
          consumer_summary: brand.consumer_summary,
          evidence_sources: brand.evidence_sources,
          confidence_score: brand.confidence_score,
          last_verified: brand.last_verified
        }))
        
        const header = Object.keys(csvData[0] || {}).join(',')
        const rows = csvData.map(row => Object.values(row || {}).map(val => `"${val}"`).join(','))
        const csv = [header, ...rows].join('\n')
        
        return {
          data: csv,
          success: true
        }
      }
    } catch (error) {
      return {
        data: '',
        success: false,
        error: `Export failed: ${error}`
      }
    }
  }
}